import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const NepalPage = () => {
  const nepalData = {
    name: 'Nepal',
    region: 'South Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Nepal | DDA | CTD / ACTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Nepal. DDA requirements, CTD/ACTD dossier expectations, dietary supplement rules, key documentation and market opportunity in an import-dependent Himalayan market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Nepal',
      subtitle: 'A lower-middle income, import-dependent market regulated by the Department of Drug Administration (DDA), with rising NCD burden and structured guidelines for medicines and dietary supplements.',
      cta: 'Start Your Nepal Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~29.7M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~5.4%' },
      { label: 'Pharma Imports (2023)', value: '~USD 311M' },
      { label: 'Regulatory Authority', value: 'DDA' },
      { label: 'Key Requirement', value: 'WHO-GMP mandatory' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Import-Dependent, Semi-Regulated',
      stats: [
        'DDA is national medicines regulator under Ministry of Health and Population',
        'Drug Registration Regulation 2038 and Drug Registration Guidance 2073 (2017) outline requirements',
        'Pharmaceutical imports ~USD 311 million in 2023',
        'India is major supplier of finished pharmaceuticals',
        'Public sector: government hospitals, health posts, central procurement',
        'Private sector: urban hospitals, clinics, extensive pharmacy network',
        'Price sensitivity strong for generics; well-positioned brands find space in urban segments',
        'Dietary Supplement Guideline (2072) governs nutraceuticals'
      ],
      opportunities: [
        'High health expenditure (5.4% of GDP) relative to income level',
        'Import reliance creates clear opportunity',
        'Growing demand for metabolic and cardiovascular products',
        'Dedicated Dietary Supplement Guideline indicates organized nutraceutical segment'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: High NCD Risk on Top of Existing Gaps',
      subtitle: 'Nepal faces a dual burden: rising NCDs with persistent malnutrition and infectious disease challenges.',
      categories: [
        {
          name: 'Non-Communicable Diseases (NCDs) — Rising Burden',
          items: [
            'Hypertension prevalence: ~19.9% of adults',
            'More than 21% overweight or obese',
            'NCD deaths per 100,000 increased from 360 to 438 (1990-2021)',
            'NCDs caused over 136,000 deaths in 2021',
            'Key contributors: cardiovascular disease, chronic respiratory disease, diabetes, cancer',
            'Pharma: Antihypertensives, antidiabetics, statins, cardiology products, NCD co-medications',
            'Nutra: Metabolic health, cardiometabolic support, antioxidants, weight management, lifestyle support'
          ]
        },
        {
          name: 'Nutrition & Infectious Disease Context',
          items: [
            'Malnutrition and micronutrient deficiencies still significant',
            'Ongoing nutrition and food security policy gaps',
            'Persistent undernutrition in some groups',
            'Infectious diseases remain relevant (TB, diarrhoeal illness)',
            'Long-term trend: dual burden of undernutrition + NCDs',
            'Pharma: Anti-infectives, paediatric antibiotics, ORS, TB adjunct therapies',
            'Nutra: Iron & folate, multivitamins, child nutrition, maternal supplements, immunity formulations'
          ]
        },
        {
          name: 'Best-Fit Portfolio Categories',
          items: [
            'Pharma: Essential generics (antihypertensives, antidiabetics, statins, antibiotics, paediatrics), NCD-centric molecules with WHO-GMP',
            'Nutra: Iron + folate, haematinics, multivitamins & minerals, metabolic health, antioxidant, joint & cardio support'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Department of Drug Administration (DDA)',
      description: 'DDA regulates medicines, pharmacies and pharmaceutical industries. Drug Act 2035 and Drug Registration Regulation 2038 provide legal foundation. Drug Registration Guidance 2073 (2017) details technical requirements aligned with ICH CTD / ASEAN CTD concepts.',
      pharma: {
        timeline: 'Varies based on dossier quality, WHO-GMP status, and potential on-site inspection',
        requirements: [
          'CTD/ACTD-aligned dossier per Medicine Registration Guidance 2073',
          'Administrative information and application forms',
          'Quality/CMC data (API, finished product, stability)',
          'Non-clinical & clinical summaries as applicable',
          'WHO-GMP certificate for specific product/site is compulsory',
          'DDA may conduct on-site inspections of non-SRA manufacturers before registration',
          'Local Nepal-registered firm acts as MAH and submits to DDA',
          'DDA assesses quality, safety and efficacy',
          'Drug registration certificate issued with defined validity',
          'Variations must be filed per DDA guidance'
        ],
        insight: 'Nepal explicitly aligns with ICH CTD / ASEAN CTD concepts. WHO-GMP is mandatory, and DDA can conduct on-site inspections for non-stringent regulatory authority manufacturers (including many Indian sites).'
      },
      nutra: {
        timeline: 'Depends on formulation compliance with Dietary Supplement Guideline',
        requirements: [
          'Regulated by Dietary Supplement Guideline (2072)',
          'Ministry of Agriculture & Livestock Development and food authorities oversight',
          'Treated as food-type products if within allowed frameworks',
          'Full qualitative and quantitative composition required',
          'COA for finished product and key raw materials',
          'GMP / quality certification of manufacturer',
          'Food-grade labelling with ingredient declaration, nutrition info',
          'No unauthorized disease-treatment claims',
          'Registration validity typically 5 years with renewal requirement'
        ],
        insight: 'Dedicated Dietary Supplement Guideline provides organized framework. Active publication of registered supplement lists indicates growing segment for vitamins, minerals and functional foods.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with DDA Nepal',
      steps: [
        'Local Agent / Importer Appointment - Nepal-registered firm acts as MAH',
        'Dossier Preparation - CTD/ACTD-aligned per Medicine Registration Guidance 2073',
        'Administrative information and application forms submission',
        'Quality/CMC data - API, finished product, stability documentation',
        'Non-clinical & clinical summaries as applicable to product type',
        'WHO-GMP Certificate - Compulsory for specific product/site',
        'Potential On-site Inspection - DDA may inspect non-SRA manufacturers',
        'DDA Assessment - Quality, safety, efficacy evaluation',
        'Query handling and deficiency responses',
        'Drug registration certificate issuance',
        'Post-approval variations filing per DDA guidance'
      ]
    },
    
    nutraPathway: {
      title: 'Dietary Supplements & Nutraceuticals in Nepal',
      steps: [
        'Classification under Dietary Supplement Guideline 2072',
        'Local importer/firm setup as applicant',
        'Full qualitative and quantitative composition preparation',
        'COA for finished product and key raw materials',
        'GMP / quality certification of manufacturer (ISO or equivalent)',
        'Food-grade labelling preparation - ingredient declaration, nutrition info',
        'Claims review - ensure no unauthorized disease-treatment claims',
        'Free Sale Certificate or equivalent for imported products',
        'Application dossier submission per Guideline 2072',
        'Registration approval (typically 5-year validity)',
        'Renewal filing before expiry'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Application forms per Drug Registration Regulation 2038',
        'CTD/ACTD-aligned Medicine Registration Dossier (Guidance 2073)',
        'Administrative data (MAH, manufacturer, licences)',
        'Quality data (API & FPP information, specs, stability)',
        'Non-clinical & clinical summaries (if applicable)',
        'WHO-GMP certificate for product/site',
        'Evidence of registration in country of origin (if available)',
        'Product samples, packaging specimens',
        'Labels/package inserts',
        'Fees and DDA-specified declarations'
      ],
      'Dietary Supplements / Nutraceuticals': [
        'Application dossier per Dietary Supplement Guideline 2072',
        'Full formula (ingredients and quantities)',
        'COA and microbiological testing results',
        'Manufacturer GMP / ISO or equivalent certifications',
        'Label artwork per Nepali food & supplement labelling rules',
        'Free Sale Certificate or equivalent for imported products'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Nepal Should Be Part of Your South Asia Strategy',
      reasons: [
        'Import-dependent: pharmaceutical imports ≈ USD 311M in 2023',
        'Limited local manufacturing capacity in scope',
        'NCD burden rising sharply: CVD, diabetes, cancers, chronic respiratory disease',
        'Structured but accessible regulatory system: clear Drug Registration Guidance 2073',
        'WHO-GMP requirements defined and predictable',
        'Dietary Supplement Guideline in place for nutraceutical approvals',
        'Strong Indian linkage: geographic and trade proximity',
        'India is natural partner for Nepal\'s pharma and nutraceutical supply'
      ],
      bestPortfolio: [
        'Pharma: Essential generics (antihypertensives, antidiabetics, statins, antibiotics, paediatrics), NCD-centric molecules with WHO-GMP sites',
        'Nutra: Iron + folate, haematinics, multivitamins & minerals, metabolic health, antioxidant, joint & cardio support'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Nepal',
      services: [
        'Portfolio & Market Fit Analysis - Map products to Nepal\'s NCD + nutrition landscape',
        'CTD/ACTD Dossier Development - Build dossiers per Drug Registration Guidance 2073',
        'WHO-GMP & Non-SRA Inspection Readiness - Prepare for DDA on-site inspection requirements',
        'Nutraceutical Strategy & Documentation - Align with Dietary Supplement Guideline 2072',
        'Label & Claims Compliance - Review against food/supplement labelling rules',
        'Local Partner & Lifecycle Support - Coordinate with Nepali importer/MAH for submissions, queries, renewals'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Nepal?',
        answer: 'The Department of Drug Administration (DDA) regulates drug manufacture, imports, distribution and registration under the Drug Act 2035 and Drug Registration Regulation 2038.'
      },
      {
        question: 'What dossier format is required for pharmaceuticals?',
        answer: 'DDA\'s Drug Registration Guidance 2073 uses a CTD/ACTD-aligned structure, with separate modules/sections for administrative data, quality, safety and efficacy.'
      },
      {
        question: 'Is WHO-GMP mandatory for foreign manufacturers?',
        answer: 'Yes. WHO-GMP certification for the concerned product/site is compulsory, and DDA may conduct on-site inspections of manufacturers from non-SRA countries before registration.'
      },
      {
        question: 'How are nutraceuticals and dietary supplements regulated?',
        answer: 'They are governed by the Dietary Supplement Guideline 2072, which specifies formulation, safety and labelling conditions. Supplements are treated as food-type products rather than medicines if they meet these criteria.'
      },
      {
        question: 'How long is supplement registration valid?',
        answer: 'Typical supplement registrations are valid for about 5 years, after which renewal is required.'
      }
    ]
  };

  return <CountryTemplate countryData={nepalData} />;
};
